import { ReactNode } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { ScrollProgress } from './ScrollProgress';
import { FloatingContactWidget } from '@/components/ui/FloatingContactWidget';
import { PageBackground } from '@/components/ui/PageBackground';
import { ScrollToTop } from '@/components/ui/ScrollToTop';

interface LayoutProps {
  children: ReactNode;
  backgroundVariant?: 'default' | 'grid' | 'gradient' | 'blueprint';
  fixedBg?: string;
}

// Note: page-transition animation lives once, at the route level, via
// <PageTransition> (src/components/ui/PageTransition.tsx) in App.tsx — every
// page already renders inside it. This Layout used to also wrap itself in a
// second, separate PageTransition (a black curtain-wipe + its own opacity
// fade), so every navigation ran two independent, out-of-sync transition
// animations at once — that's what caused the black/white flash between
// pages. Layout no longer does its own transition.
export function Layout({ children, backgroundVariant = 'default', fixedBg }: LayoutProps) {
  return (
    <PageBackground variant={backgroundVariant}>
      {fixedBg && (
        <div
          className="fixed inset-0 z-0 bg-cover bg-center bg-fixed"
          style={{ backgroundImage: `url(${fixedBg})` }}
        >
          <div className="absolute inset-0 bg-background/80 backdrop-blur-[2px]" />
        </div>
      )}
      <div className="min-h-screen flex flex-col overflow-x-hidden relative">
        <ScrollProgress />
        <Header />
        <main className="flex-1 relative z-10">{children}</main>
        <div className="relative z-10 mt-4">
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.2) 48%, rgba(255,255,255,0.36) 100%)',
              backdropFilter: 'blur(24px) saturate(145%)',
              WebkitBackdropFilter: 'blur(24px) saturate(145%)',
              borderTop: '1px solid rgba(255,255,255,0.6)',
              borderBottom: '1px solid rgba(148,163,184,0.35)',
            }}
          />
          <Footer />
        </div>
        <FloatingContactWidget />
        <ScrollToTop />
      </div>
    </PageBackground>
  );
}
